import React from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import Skeleton from '@mui/material/Skeleton';
import CloseIcon from '@mui/icons-material/CloseRounded';
import AddIcon from '@mui/icons-material/AddRounded';
import { useTranslation } from 'react-i18next';

import { LanguageSwitcher } from 'components/Shared/LanguageSwitcher';
import { Button } from 'components/Shared/Inputs';
import { useNavigationUtils } from '../../hooks';
import { useAuth } from 'components/Auth';
import { useNavigationGuard } from 'providers/NavigationGuard';
import { useFeatureManager } from 'hooks/useFeatureManager';

import './MobileDrawer.scss';

interface MobileDrawerProps {
    isOpen: boolean,
    onClose: () => void,
    onOpen: () => void
}

export const MobileDrawer: React.FC<MobileDrawerProps> = (props) => {
    const { isOpen, onClose, onOpen } = props;
    const { authenticated, logout } = useAuth();
    const { guardAction } = useNavigationGuard();
    const { t } = useTranslation('navigation', { useSuspense: false });
    
    const { pathRoot, showCalendarTab, showInvoicingTab, showStaffTab, showCriticalIncidentTabs, showCICasesTab, showEAPTabs, loading } = useNavigationUtils();
    
    const { isAdditionalHoursFeatureEnabled, isListReferralFeatureEnabled } = useFeatureManager();

    const renderCloseButton = () => (
        <IconButton aria-label="close_button" onClick={onClose}>
            <CloseIcon />
        </IconButton>
    )

    const renderLink = (id: string, path: string) => (
        loading 
        ? <Skeleton className="navbar_item skeleton" variant="text" />
        : (<ListItemButton className={clsx('navbar_item', path === pathRoot && 'active')} id={id}
                component={Link} to={path} onClick={onClose}
            >
                {t(id)}
            </ListItemButton>
        )
    )

    const renderAddClientButton = () => (
        <ListItemButton className={clsx('navbar_item', pathRoot === '/add-clients' && 'active')} id={'add-client'}
            component={Link} to={'/add-clients'} onClick={onClose}
        >
            <AddIcon/>
            {t('add-client')}
        </ListItemButton>
    )

    const handleLogout = () => {
        onClose();
        guardAction(logout);
    }
    
    return (
        <SwipeableDrawer
            anchor='left'
            open={isOpen}
            onClose={onClose}
            onOpen={onOpen}
            className="mobile_drawer"
        >
            <div className="mobile_drawer_toolbar">
                {renderCloseButton()}
                <LanguageSwitcher component={Button} variant="text" />
            </div>

            <List component="nav" className="mobile_drawer_nav_list" disablePadding>
                {
                    authenticated &&
                    <>
                        {renderLink('dashboard', '/')}
                        {showCriticalIncidentTabs && renderLink('referrals', '/critical_incident/referrals')}
                        {showCICasesTab && renderLink('ci_cases', '/critical_incident/cases')}
                        {showEAPTabs && renderLink('to-dos', '/todos')}
                        {showEAPTabs && renderLink('clients', '/clients')}
                        {showStaffTab && renderLink('staff', '/providers')}
                        {showEAPTabs && showCalendarTab && renderLink('calendar', '/calendar')}
                        {showInvoicingTab && renderLink('invoicing', '/invoicing')}
                        {showEAPTabs && isAdditionalHoursFeatureEnabled() && renderLink('requests', '/requests')}
                        <Divider />
                        {
                            isListReferralFeatureEnabled() &&
                            <>
                                {renderAddClientButton()}
                            </>
                        }
                        <Divider />
                    </>
                }
                {renderLink('learn', '/learn')}
                {renderLink('faq', '/faq')}
                {renderLink('help', '/help')}
                {
                    authenticated &&
                    <ListItem className="navbar_item" button onClick={handleLogout}>
                        {t("logout")}
                    </ListItem>
                }
            </List>
        </SwipeableDrawer>
    )
}

